'use client'

import { useEffect, useRef, useState } from 'react'
import {
  FaPhoneSlash,
  FaMicrophone,
  FaArrowUp,
  FaVolumeUp,
  FaTimes,
  FaUser,
} from 'react-icons/fa'
import { getIdToken } from 'firebase/auth'
import { pickRecorderType, blobToWavBase64 } from '../helpers/voiceAudio'
import '../voiceRecorder.css'

// Timing — mirrors promptchan-mobile/screens/VoiceCall.jsx
const RING_MS = 2500
const HANGUP_MAX_MS = 1500
const PLAY_MAX_MS = 45000
const MAX_RECORD_MS = 60000 // web safety cap (mobile is fully tap-controlled)

// Colors — mirrors mobile constants/colors.js
const GREEN = '#22C55E'
const RED = '#E5484D'
const ACCENT = '#4F46E5'
const FAINT = 'rgba(255,255,255,0.22)'
const SURFACE_ALT = '#3A3B42'

const SFX = {
  ringing: '/assets/sounds/ringing.mp3',
  connected: '/assets/sounds/connected.mp3',
  hangup: '/assets/sounds/hangup.mp3',
  shutter: '/assets/sounds/shutter.mp3',
}

function resolveAvatar(character) {
  if (!character) return null
  if (typeof character.portraitImage === 'string' && character.portraitImage) {
    return character.portraitImage
  }
  if (Array.isArray(character.imageUrl)) return character.imageUrl[0]
  if (typeof character.imageUrl === 'string') return character.imageUrl
  return null
}

const firstName = (name) => (name ? String(name).split(' ')[0] : 'Call')

const fmtTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

const VoiceRecorder = ({
  user,
  currentMatch,
  isRoleplay = true,
  language = 'en',
  setCallModalOpen,
  isErrorModalOpen,
}) => {
  const [status, setStatus] = useState('connecting') // connecting | ringing | connected | ended
  const [phase, setPhase] = useState('idle') // idle | recording | thinking | speaking
  const [micDenied, setMicDenied] = useState(false)
  const [unsupported, setUnsupported] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [selfie, setSelfie] = useState(null)
  const [callError, setCallError] = useState(null)

  const character = currentMatch
  const avatar = resolveAvatar(character)
  const characterId = character?.id
  const name = firstName(character?.name)

  const endedRef = useRef(false)
  const startedRef = useRef(false)
  const phaseRef = useRef('idle')
  const transitionRef = useRef(false)

  const streamRef = useRef(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const recordTimeoutRef = useRef(null)

  const ringTimeoutRef = useRef(null)
  const voicePlayerRef = useRef(null)
  const voiceFinishRef = useRef(null)
  const sfxPlayerRef = useRef(null)
  const thinkingClipRef = useRef(null)
  const thinkingPlayerRef = useRef(null)

  const setPhaseSync = (p) => {
    phaseRef.current = p
    setPhase(p)
  }

  // ---------- audio playback (HTMLAudioElement) ----------
  const stopVoicePlayer = () => {
    if (voiceFinishRef.current) {
      voiceFinishRef.current()
      return
    }
    try {
      voicePlayerRef.current?.pause?.()
    } catch {}
    voicePlayerRef.current = null
  }

  const stopSfx = () => {
    try {
      sfxPlayerRef.current?.pause?.()
    } catch {}
    sfxPlayerRef.current = null
  }

  // Pre-fetch a short character-voiced "thinking" clip while the user is still
  // recording, so it can play the instant they hit send (filling the gap before
  // the reply) — instead of after a network round-trip that lands right as the
  // reply audio is ready.
  const prefetchThinking = async () => {
    thinkingClipRef.current = null
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_greeting_v2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            gender: character?.gender || 'Woman',
            voice: character?.voice,
            thinking: true,
          }),
        },
      )
      const data = await res.json().catch(() => null)
      if (res.ok && data?.audio && !endedRef.current) {
        thinkingClipRef.current = data.audio
      }
    } catch {}
  }

  const playThinking = () => {
    const src = thinkingClipRef.current
    thinkingClipRef.current = null // consume; next turn pre-fetches a fresh one
    if (!src || endedRef.current) return
    try {
      const audio = new Audio(src)
      thinkingPlayerRef.current = audio
      audio.play().catch(() => {})
    } catch {}
  }

  const stopThinking = () => {
    try {
      thinkingPlayerRef.current?.pause?.()
    } catch {}
    thinkingPlayerRef.current = null
  }

  const playToCompletion = (src) =>
    new Promise((resolve) => {
      if (endedRef.current || !src) {
        resolve()
        return
      }
      let audio
      try {
        audio = new Audio(src)
      } catch {
        resolve()
        return
      }
      voicePlayerRef.current = audio
      let done = false
      let safety = null
      const finish = () => {
        if (done) return
        done = true
        voiceFinishRef.current = null
        if (safety) clearTimeout(safety)
        try {
          audio.onended = null
          audio.onerror = null
          audio.pause()
        } catch {}
        if (voicePlayerRef.current === audio) voicePlayerRef.current = null
        resolve()
      }
      voiceFinishRef.current = finish
      audio.onended = finish
      audio.onerror = finish
      audio.play().catch(() => finish())
      safety = setTimeout(finish, PLAY_MAX_MS)
    })

  const playSfx = (src, loop = false) => {
    stopSfx()
    try {
      const audio = new Audio(src)
      audio.loop = loop
      sfxPlayerRef.current = audio
      audio.play().catch(() => {})
    } catch {}
  }

  const playHangup = () =>
    new Promise((resolve) => {
      let audio
      try {
        audio = new Audio(SFX.hangup)
      } catch {
        resolve()
        return
      }
      sfxPlayerRef.current = audio
      let done = false
      const finish = () => {
        if (done) return
        done = true
        try {
          audio.onended = null
          audio.pause()
        } catch {}
        if (sfxPlayerRef.current === audio) sfxPlayerRef.current = null
        resolve()
      }
      audio.onended = finish
      audio.play().catch(() => finish())
      setTimeout(finish, HANGUP_MAX_MS)
    })

  // ---------- mic / recording ----------
  const ensureStream = async () => {
    if (streamRef.current && streamRef.current.active) return streamRef.current
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { noiseSuppression: true, echoCancellation: true },
    })
    // If the call ended while the permission prompt / getUserMedia was in
    // flight, stop the freshly-acquired tracks instead of leaking a live mic
    // (cleanup() already ran and saw streamRef.current === null).
    if (endedRef.current) {
      try {
        stream.getTracks().forEach((t) => t.stop())
      } catch {}
      return null
    }
    streamRef.current = stream
    return stream
  }

  const stopRecorderAndGetBlob = (recorder) =>
    new Promise((resolve) => {
      if (!recorder || recorder.state === 'inactive') {
        resolve(null)
        return
      }
      recorder.onstop = () => {
        const type =
          recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        chunksRef.current = []
        resolve(blob)
      }
      try {
        recorder.stop()
      } catch {
        resolve(null)
      }
    })

  const startRecording = async () => {
    if (endedRef.current) return
    setCallError(null)
    setPhaseSync('recording')
    prefetchThinking() // ready to play the moment they hit send
    try {
      const stream = await ensureStream()
      if (endedRef.current || !stream) return
      const picked = pickRecorderType()
      const recorder = picked
        ? new MediaRecorder(stream, { mimeType: picked.mimeType })
        : new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorderRef.current = recorder
      recorder.start()
      recordTimeoutRef.current = setTimeout(() => {
        if (phaseRef.current === 'recording') onPrimaryPress()
      }, MAX_RECORD_MS)
    } catch (e) {
      if (endedRef.current) return
      console.error('Could not start recording:', e)
      // Transient mid-call failure (device unplugged, recorder hiccup): fall
      // back to idle so the user can tap to retry, rather than latching the
      // permission card. Initial permission denial is handled in startCall.
      setPhaseSync('idle')
    }
  }

  const stopAndSend = async () => {
    setPhaseSync('thinking')
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current)
      recordTimeoutRef.current = null
    }
    const recorder = recorderRef.current
    let blob = null
    try {
      blob = await stopRecorderAndGetBlob(recorder)
    } catch {}
    if (endedRef.current) return
    if (!blob || blob.size === 0) {
      await startRecording()
      return
    }
    // Play the pre-fetched "thinking" clip right away, in parallel with the
    // upload + reply generation, so it fills the silence after send.
    playThinking()
    try {
      // Transcode the recording to WAV — the exact format the web endpoint
      // received before (no userVoiceFormat field → backend defaults to "wav").
      const b64 = await blobToWavBase64(blob)
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_chat_v2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            message: '',
            characterId,
            userVoice: b64,
            isRoleplay,
            audioMessagesEnabled: true,
            selfiePhotosEnabled: false,
            send_date: new Date().toISOString(),
            language,
          }),
        },
      )
      const data = await res.json().catch(() => null)
      if (endedRef.current) return
      if (!res.ok) throw new Error(data?.error || 'Voice message failed')
      stopThinking() // reply is ready — stop the filler before it plays
      if (data?.selfie) setSelfie(data.selfie)
      if (data?.audio) {
        setPhaseSync('speaking')
        await playToCompletion(data.audio)
      }
      if (endedRef.current) return
      await startRecording()
    } catch (e) {
      if (endedRef.current) return
      stopThinking()
      console.error('Voice message failed:', e)
      setCallError(
        e?.message && e.message !== 'Voice message failed'
          ? `Couldn’t send: ${e.message}. Tap to try again.`
          : 'Voice message failed. Tap to try again.',
      )
      setPhaseSync('idle')
    }
  }

  const onPrimaryPress = () => {
    if (transitionRef.current) return
    const p = phaseRef.current
    if (p === 'recording') {
      transitionRef.current = true
      stopAndSend().finally(() => {
        transitionRef.current = false
      })
    } else if (p === 'idle') {
      transitionRef.current = true
      startRecording().finally(() => {
        transitionRef.current = false
      })
    }
  }

  // ---------- call lifecycle ----------
  const cleanup = () => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current)
      ringTimeoutRef.current = null
    }
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current)
      recordTimeoutRef.current = null
    }
    stopVoicePlayer()
    stopSfx()
    stopThinking()
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.onstop = null
        recorderRef.current.stop()
      }
    } catch {}
    recorderRef.current = null
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop())
      } catch {}
      streamRef.current = null
    }
  }

  const startCall = async () => {
    endedRef.current = false
    setStatus('connecting')
    setPhaseSync('idle')
    setElapsed(0)

    // Voice calls need both mic capture and MediaRecorder. Some browsers
    // (older WebKit, embedded webviews) expose getUserMedia but not
    // MediaRecorder — surface a distinct message rather than a mic-permission one.
    if (
      typeof MediaRecorder === 'undefined' ||
      !navigator?.mediaDevices?.getUserMedia
    ) {
      setUnsupported(true)
      return
    }

    try {
      await ensureStream()
    } catch {
      setMicDenied(true)
      return
    }
    if (endedRef.current) return

    setMicDenied(false)
    setStatus('ringing')
    playSfx(SFX.ringing, true)

    let greetingAudio = null
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_FREE}/api/generate_greeting_v2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            gender: character?.gender || 'Woman',
            voice: character?.voice,
            thinking: false,
          }),
        },
      )
      const data = await res.json().catch(() => null)
      if (res.ok && data?.audio) greetingAudio = data.audio
    } catch {}
    if (endedRef.current) return

    ringTimeoutRef.current = setTimeout(async () => {
      if (endedRef.current) return
      stopSfx()
      playSfx(SFX.connected)
      setStatus('connected')
      if (greetingAudio) {
        setPhaseSync('speaking')
        await playToCompletion(greetingAudio)
        if (endedRef.current) return
      }
      await startRecording()
    }, RING_MS)
  }

  const stopCall = async () => {
    if (endedRef.current) {
      setCallModalOpen(false)
      return
    }
    endedRef.current = true
    setStatus('ended')
    cleanup()
    await playHangup()
    setCallModalOpen(false)
  }

  // start the call on mount; tear everything down on unmount
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true
      startCall()
    }
    return () => {
      endedRef.current = true
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // an external error modal ends the call
  useEffect(() => {
    if (isErrorModalOpen) stopCall()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorModalOpen])

  // call timer
  useEffect(() => {
    if (status !== 'connected') return
    const id = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(id)
  }, [status])

  // ---------- derived UI ----------
  const connected = status === 'connected'
  const recording = phase === 'recording'
  const thinking = phase === 'thinking'
  const speaking = phase === 'speaking'
  const yourTurn = connected && recording

  const ringColor = yourTurn
    ? GREEN
    : connected && (speaking || thinking)
      ? ACCENT
      : FAINT
  const haloActive = connected && (recording || speaking || thinking)
  const haloColor = yourTurn ? GREEN : ACCENT

  let pillText = ''
  let pillDot = null
  if (!micDenied && !unsupported) {
    if (status === 'ringing') pillText = 'Ringing…'
    else if (status === 'connecting') pillText = 'Connecting…'
    else if (recording) {
      pillText = 'Your turn'
      pillDot = GREEN
    } else if (thinking) pillText = `${name} is thinking…`
    else if (speaking) {
      pillText = `${name} is speaking`
      pillDot = ACCENT
    }
  }

  let primary = null
  if (connected && !micDenied && !unsupported) {
    if (recording)
      primary = { kind: 'send', label: 'Send', bg: GREEN, disabled: false }
    else if (thinking)
      primary = {
        kind: 'spinner',
        label: 'Thinking…',
        bg: SURFACE_ALT,
        disabled: true,
      }
    else if (speaking)
      primary = {
        kind: 'volume',
        label: 'Listening to reply',
        bg: SURFACE_ALT,
        disabled: true,
      }
    else
      primary = {
        kind: 'mic',
        label: 'Tap to talk',
        bg: ACCENT,
        disabled: false,
      }
  }

  const statusWord =
    status === 'ringing' ? 'Ringing…' : status === 'ended' ? '' : 'Connecting…'

  const renderPrimaryIcon = (kind) => {
    if (kind === 'spinner') return <span className="voice-spinner" />
    if (kind === 'send') return <FaArrowUp size={28} />
    if (kind === 'volume') return <FaVolumeUp size={26} />
    return <FaMicrophone size={28} />
  }

  return (
    <div className="relative w-full h-full overflow-hidden flex flex-col text-white bg-black">
      <div className="relative z-10 flex flex-col items-center justify-between h-full w-full py-8 px-5">
        {/* header */}
        <div className="flex flex-col items-center gap-1">
          <h2 className="text-2xl md:text-3xl font-bold text-center truncate max-w-[80vw]">
            {character?.name || 'Call'}
          </h2>
          <p className="text-sm text-white/70 tabular-nums">
            {connected ? fmtTime(elapsed) : statusWord}
          </p>
        </div>

        {/* center */}
        <div className="flex flex-col items-center gap-5">
          <div
            className="relative flex items-center justify-center"
            style={{ width: 224, height: 224 }}
          >
            <div
              className={`voice-halo ${haloActive ? 'active' : ''}`}
              style={{ backgroundColor: haloColor }}
            />
            <div
              className="rounded-full flex items-center justify-center overflow-hidden relative"
              style={{
                width: 168,
                height: 168,
                border: `3px solid ${ringColor}`,
                zIndex: 1,
              }}
            >
              {avatar ? (
                <img
                  src={avatar}
                  alt={character?.name || ''}
                  className="rounded-full object-cover object-top"
                  style={{ width: 156, height: 156 }}
                />
              ) : (
                <div
                  className="rounded-full flex items-center justify-center bg-[#3A3B42]"
                  style={{ width: 156, height: 156 }}
                >
                  <FaUser size={56} className="text-white/50" />
                </div>
              )}
            </div>
          </div>

          {unsupported ? (
            <div className="flex flex-col items-center gap-2 px-4 text-center max-w-sm">
              <p className="font-semibold">Voice calls aren’t supported here</p>
              <p className="text-sm text-white/70">
                Your browser doesn’t support audio recording. Try the latest
                Chrome, Edge, Firefox, or Safari.
              </p>
            </div>
          ) : micDenied ? (
            <div className="flex flex-col items-center gap-2 px-4 text-center max-w-sm">
              <img
                src="/assets/voice/permissions.webp"
                alt=""
                className="rounded-xl mb-2 max-w-[240px]"
              />
              <p className="font-semibold">Microphone access needed</p>
              <p className="text-sm text-white/70">
                Enable microphone permission in your browser to use voice calls.
              </p>
            </div>
          ) : pillText ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10">
              {pillDot && (
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: pillDot }}
                />
              )}
              <span className="text-sm font-semibold">{pillText}</span>
            </div>
          ) : null}

          {yourTurn && (
            <p className="text-[13px] text-white/60 -mt-2">
              Speak, then tap send
            </p>
          )}

          {callError && !micDenied && !unsupported && (
            <p className="text-[13px] text-red-400 text-center max-w-xs">
              {callError}
            </p>
          )}
        </div>

        {/* controls */}
        <div className="flex items-start justify-center gap-10">
          {primary && (
            <div className="flex flex-col items-center gap-2.5 min-w-[96px]">
              <button
                type="button"
                onClick={onPrimaryPress}
                disabled={primary.disabled}
                aria-label={primary.label}
                className={`voice-btn ${primary.disabled ? 'opacity-70' : ''}`}
                style={{ backgroundColor: primary.bg }}
              >
                {renderPrimaryIcon(primary.kind)}
              </button>
              <span className="text-[13px] font-semibold text-white/85">
                {primary.label}
              </span>
            </div>
          )}

          <div className="flex flex-col items-center gap-2.5 min-w-[96px]">
            <button
              type="button"
              onClick={stopCall}
              aria-label="End call"
              className="voice-btn"
              style={{ backgroundColor: RED }}
            >
              <FaPhoneSlash size={22} />
            </button>
            <span className="text-[13px] font-semibold text-white/85">End</span>
          </div>
        </div>
      </div>

      {/* selfie popup (web extra — backend may return data.selfie) */}
      {selfie && (
        <div className="absolute z-20 top-4 left-1/2 -translate-x-1/2 p-2 bg-[var(--middle-bg-color)] rounded-xl shadow-md voice-fade-in w-80 max-w-[90%]">
          <button
            type="button"
            onClick={() => setSelfie(null)}
            aria-label="Close photo"
            className="absolute right-3 top-3 text-white z-30 text-xl"
          >
            <FaTimes />
          </button>
          <img
            src={selfie}
            alt=""
            className="rounded-xl w-full"
            onLoad={() => {
              try {
                new Audio(SFX.shutter).play().catch(() => {})
              } catch {}
            }}
          />
        </div>
      )}
    </div>
  )
}

export default VoiceRecorder
